import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  MapLocationData,
  LocationMode,
  MapMode,
  LocationChangeHandler,
} from '@hero/web-components';
import { Id, SavedLocation } from '@hero/schema';
import { convertToLatLng } from '@hero/core';
import { useAddressApi } from '@hero/api-hooks';

export interface UseMapLocationsOptions {
  /** Initial locations */
  initialLocations?: MapLocationData[];
  /** Location mode: 'single' or 'multi' */
  locationMode?: LocationMode;
  /** Maximum number of markers allowed (undefined = unlimited) */
  markerCountLimit?: number;
  /** Current map mode */
  mode?: MapMode;
  /** Map instance */
  map?: google.maps.Map | null;
  /** Whether map is loaded and ready */
  isLoaded?: boolean;
  /** Smooth zoom function */
  smoothZoomToLocation?: (
    map: google.maps.Map,
    position: google.maps.LatLngLiteral,
    zoom?: number,
  ) => void;
  /** Fit bounds function */
  fitBounds?: (map: google.maps.Map, locations: MapLocationData[]) => void;
  /** Location change callback */
  onLocationChange?: LocationChangeHandler;
  /** Location edit start callback */
  onLocationEditStart?: () => void;
  /** Location edit end callback; receives finalized location when user clicks done (green check) */
  onLocationEditEnd?: (location?: MapLocationData) => void;
  /** Callback to trigger edit mode (for auto-enter functionality) */
  onAutoEnterEditMode?: (index: number) => void;
  /** If true, automatically enter edit mode on the first location or create default location */
  enterEditModeInitially?: boolean;
  /** Organization ID (for save location modal context) */
  orgId?: string;
}

/**
 * Hook for managing map locations state and handlers
 * Combines location state management with location manipulation handlers
 */
export function useMapLocations(options: UseMapLocationsOptions = {}) {
  const {
    initialLocations = [],
    locationMode = 'multi',
    markerCountLimit: initialMarkerCountLimit,
    mode,
    map,
    isLoaded = false,
    smoothZoomToLocation,
    fitBounds,
    onLocationChange,
    onLocationEditStart,
    onLocationEditEnd,
    onAutoEnterEditMode,
    enterEditModeInitially = false,
    orgId,
  } = options;

  const addressApi = useAddressApi();

  // Determine marker count limit based on location mode
  const markerCountLimit = useMemo(() => {
    if (initialMarkerCountLimit !== undefined) {
      return initialMarkerCountLimit;
    }
    return locationMode === 'single' ? 1 : undefined;
  }, [locationMode, initialMarkerCountLimit]);

  const [locations, setLocations] = useState<MapLocationData[]>(initialLocations);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const originalLocationRef = useRef<MapLocationData | null>(null);
  const hasAutoEnteredEditMode = useRef(false);
  /** Guard: avoid starting a second address fetch for the same coords (e.g. effect runs twice or Strict Mode) */
  const fetchingAddressForRef = useRef<{ lat: number; lng: number } | null>(null);

  // Sync internal state when parent passes new locations (e.g. SavedLocationMap when saved location loads in edit modal, or order map when order data changes).
  // When coords match and current location already has address (e.g. from search select or map click), keep that address so we don't overwrite it with stale initialAddress from parent (single-location map only passes coords + initialAddress; form doesn't store full address).
  useEffect(() => {
    setLocations(prev => {
      if (!initialLocations?.length) return initialLocations ?? [];
      return initialLocations.map((init, i) => {
        const current = prev[i];
        const sameCoords =
          current?.cords &&
          init.cords &&
          current.cords.lat === init.cords.lat &&
          current.cords.lng === init.cords.lng;
        const currentHasAddress = !!(current?.fullAddress || current?.address);
        if (sameCoords && current) {
          // Preserve internal state that may have been updated (e.g. by handleLocationSaved, search, drag)
          // while parent hasn't re-rendered with the new data yet.
          const currentHasSavedLocation = !!(current.savedLocation || current.savedLocationId);
          return {
            ...init,
            ...(currentHasAddress && {
              fullAddress: current.fullAddress,
              address: current.address,
              city: current.city,
            }),
            ...(currentHasSavedLocation && {
              savedLocation: current.savedLocation,
              savedLocationId: current.savedLocationId,
            }),
            isAccurateLocation: current.isAccurateLocation,
            locationUrl: current.locationUrl ?? init.locationUrl,
          };
        }
        return init;
      });
    });
  }, [initialLocations]);

  /**
   * Add a new location
   * In single mode, replaces existing location
   */
  const addLocation = useCallback(
    (location: MapLocationData) => {
      setLocations(prev => {
        if (markerCountLimit === 1) {
          // Single mode: replace existing
          return [location];
        }
        // Multi mode: add new
        return [...prev, location];
      });
    },
    [markerCountLimit],
  );

  /**
   * Enter edit mode for a specific marker
   */
  const enterEditMode = useCallback((index: number) => {
    setEditingIndex(index);
  }, []);

  /**
   * Exit edit mode
   */
  const exitEditMode = useCallback(() => {
    setEditingIndex(null);
  }, []);

  // Auto-enter edit mode logic
  // Two scenarios:
  // 1. If enterEditModeInitially is true and location exists: enter edit mode for that location
  // 2. If enterEditModeInitially is true and no location exists: create default location and enter edit mode
  useEffect(() => {
    // Only proceed if enterEditModeInitially is enabled
    if (!enterEditModeInitially) {
      return;
    }

    // Only work in single location mode
    if (locationMode !== 'single') {
      return;
    }

    // `view` is read-only. `edit` and `add_new_location` both mean the user
    // came here to place a pin, so both auto-open the card and reverse-geocode
    // the pin they opened on — without it an initial pin (e.g. coordinates
    // parsed from a Maps link) renders a card with no address, and every
    // click/drag falls back to the path that waits for the geocode before the
    // marker moves.
    if (mode === 'view') {
      return;
    }

    // Map must be loaded and ready
    if (!isLoaded || !map) {
      return;
    }

    // Don't proceed if already in edit mode or already auto-entered
    if (editingIndex !== null || hasAutoEnteredEditMode.current) {
      return;
    }

    // Scenario 1: Location exists - enter edit mode for it
    if (locations.length > 0 && locations[0]) {
      hasAutoEnteredEditMode.current = true;
      const location = locations[0];

      // Check if location has coordinates but no address - fetch address if needed
      const hasCoordinates = location.cords && location.cords.lat && location.cords.lng;
      const hasAddress = location.fullAddress || location.address;

      if (hasCoordinates && !hasAddress) {
        // Only fetch in true single-location context (e.g. save-location modal).
        // Skip when multiple locations exist so we don't fetch for "card X" from the order map
        // (which has cards X, Y, ...) and trigger an extra call for the first card only.
        if (locations.length !== 1) {
          enterEditMode(0);
          if (onLocationEditStart) onLocationEditStart();
          if (map && location && smoothZoomToLocation) smoothZoomToLocation(map, location.cords, 15);
          return;
        }
        const { lat, lng } = location.cords;
        if (
          fetchingAddressForRef.current?.lat === lat &&
          fetchingAddressForRef.current?.lng === lng
        ) {
          return;
        }
        fetchingAddressForRef.current = { lat, lng };

        addressApi
          .mutateAsync({ lat, lng })
          .then(addressData => {
            const updatedLocation: MapLocationData = {
              ...location,
              address: addressData,
              fullAddress: addressData.fullAddress,
              city: addressData.city?.name,
              isLoadingAddress: false,
            };

            setLocations(prev => {
              if (locationMode === 'single') {
                return [updatedLocation];
              } else {
                const newLocations = [...prev];
                newLocations[0] = updatedLocation;
                return newLocations;
              }
            });

            if (onLocationChange) {
              onLocationChange(updatedLocation, 0);
            }

            enterEditMode(0);
            if (onLocationEditStart) {
              onLocationEditStart();
            }
            if (map && smoothZoomToLocation) {
              smoothZoomToLocation(map, updatedLocation.cords, 15);
            }
          })
          .catch(error => {
            console.error('Failed to fetch address for location:', error);
            enterEditMode(0);
            if (onLocationEditStart) {
              onLocationEditStart();
            }
            if (map && location && smoothZoomToLocation) {
              smoothZoomToLocation(map, location.cords, 15);
            }
          })
          .finally(() => {
            fetchingAddressForRef.current = null;
          });
      } else {
        // Address already exists - just enter edit mode
        setTimeout(() => {
          enterEditMode(0);
          if (onLocationEditStart) {
            onLocationEditStart();
          }
          // Zoom to location if map is available
          if (map && location && smoothZoomToLocation) {
            smoothZoomToLocation(map, location.cords, 15);
          }
        }, 100);
      }
      return;
    }

    // Scenario 2: No location exists - create default location and enter edit mode
    if (locations.length === 0 && onAutoEnterEditMode) {
      hasAutoEnteredEditMode.current = true;
      // Use setTimeout to ensure map is fully initialized
      setTimeout(() => {
        onAutoEnterEditMode(0);
      }, 100);
    }
  }, [
    enterEditModeInitially,
    isLoaded,
    map,
    locationMode,
    mode,
    locations.length,
    locations,
    editingIndex,
    onAutoEnterEditMode,
    enterEditMode,
    onLocationEditStart,
    smoothZoomToLocation,
    addressApi.mutateAsync,
    setLocations,
    onLocationChange,
  ]);

  // Handle location selection from search
  const handleLocationSelect = useCallback(
    (location: MapLocationData, index: number) => {
      // If in single location edit mode, update the location being edited.
      // Pan to new location; zoom to min level only if currently more zoomed out (so we don't zoom again when already at that level).
      if (editingIndex !== null) {
        const updatedLocations = [...locations];
        updatedLocations[editingIndex] = location;
        setLocations(updatedLocations);

        if (map) {
          map.panTo(location.cords);
          const currentZoom = map.getZoom();
          if (typeof currentZoom === 'number' && currentZoom < 15) {
            map.setZoom(15);
          }
        }

        if (onLocationChange) {
          onLocationChange(location, editingIndex);
        }
        return;
      }

      // In 'edit' mode with editingIndex set, update the location at that index.
      if (mode === 'edit' && editingIndex !== null && editingIndex !== undefined) {
        const updatedLocations = [...locations];
        updatedLocations[editingIndex] = location;
        setLocations(updatedLocations);

        if (map) {
          map.panTo(location.cords);
          const currentZoom = map.getZoom();
          if (typeof currentZoom === 'number' && currentZoom < 15) {
            map.setZoom(15);
          }
        }

        if (onLocationChange) {
          onLocationChange(location, editingIndex);
        }
        return;
      }

      // In 'view' mode, don't allow any changes
      if (mode === 'view') {
        return;
      }

      // Normal location selection (only in 'add_new_location' mode)
      const newIndex = locationMode === 'single' ? 0 : locations.length;

      if (locationMode === 'single') {
        setLocations([location]);
      } else {
        addLocation(location);
      }

      if (onLocationChange) {
        onLocationChange(location, newIndex);
      }
    },
    [
      editingIndex,
      locationMode,
      mode,
      addLocation,
      setLocations,
      locations,
      onLocationChange,
      map,
    ],
  );

  // Handle card click (smooth zoom)
  const handleCardClick = useCallback(
    (index: number) => {
      const location = locations[index];
      if (location && map && smoothZoomToLocation) {
        smoothZoomToLocation(map, location.cords, 15);
      }
    },
    [locations, map, smoothZoomToLocation],
  );

  /**
   * Record the user's accuracy decision for a stop.
   *
   * `patch` exists for the case where the decision *came with* a better pin —
   * the user pasted a Google Maps link, so the accuracy answer and the new
   * coordinates / address / URL have to land in one update. Splitting them
   * across two calls would have the second one build from a stale
   * `locations` closure and drop the first.
   */
  const handleAccurateLocationToggle = useCallback(
    (index: number, isOrigin: boolean, isAccurate: boolean, patch?: Partial<MapLocationData>) => {
      const actualIndex = index;

      // Build updated location and update state. Call onLocationChange after setState
      // (not inside the updater) so parent e.g. form.setValue doesn't run during commit.
      const updatedLocation = {
        ...locations[actualIndex],
        ...patch,
        isAccurateLocation: isAccurate,
      };
      setLocations(prev => {
        const newLocations = [...prev];
        newLocations[actualIndex] = updatedLocation;
        return newLocations;
      });
      // Follow the pin when it moved, or the user confirms a stop and the
      // marker they were just looking at is somewhere off-screen.
      if (patch?.cords && map && smoothZoomToLocation) {
        smoothZoomToLocation(map, patch.cords, 15);
      }
      if (onLocationChange) {
        onLocationChange(updatedLocation, actualIndex);
      }
    },
    [setLocations, onLocationChange, locations, map, smoothZoomToLocation],
  );

  // Handle location edit start
  const handleLocationEdit = useCallback(
    (index: number, isOrigin: boolean) => {
      // Index is already the correct actual index from LocationCards
      const actualIndex = index;
      if (locations[actualIndex]) {
        originalLocationRef.current = { ...locations[actualIndex] };
      }

      enterEditMode(actualIndex);

      if (map && locations[actualIndex] && smoothZoomToLocation) {
        const location = locations[actualIndex];
        smoothZoomToLocation(map, location.cords, 15);
      }

      if (onLocationEditStart) {
        onLocationEditStart();
      }
    },
    [locations, enterEditMode, map, smoothZoomToLocation, onLocationEditStart],
  );

  // Handle location edit cancel
  const handleLocationCancel = useCallback(
    (index: number, isOrigin: boolean) => {
      // Index is already the correct actual index from LocationCards
      const actualIndex = index;

      if (originalLocationRef.current) {
        const newLocations = [...locations];
        newLocations[actualIndex] = originalLocationRef.current;
        setLocations(newLocations);

        if (onLocationChange) {
          onLocationChange(originalLocationRef.current, actualIndex);
        }

        originalLocationRef.current = null;
      }

      exitEditMode();

      setTimeout(() => {
        if (map && locations.length >= 2 && fitBounds) {
          fitBounds(map, locations);
        }
      }, 200);

      if (onLocationEditEnd) {
        onLocationEditEnd();
      }
    },
    [locations, setLocations, exitEditMode, map, fitBounds, onLocationChange, onLocationEditEnd],
  );

  // Handle location edit done (green check). Location already has isAccurateLocation: false if coords changed during editing.
  const handleLocationDone = useCallback(
    (index: number, isOrigin: boolean) => {
      const current = locations[index];
      originalLocationRef.current = null;
      exitEditMode();

      setTimeout(() => {
        if (map && locations.length >= 2 && fitBounds) {
          fitBounds(map, locations);
        }
      }, 200);

      if (onLocationEditEnd) {
        onLocationEditEnd(current);
      }
    },
    [locations, exitEditMode, map, fitBounds, onLocationEditEnd],
  );

  // ========== SAVE LOCATION FUNCTIONALITY ==========
  // State for LocationEditModal
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [locationModalCoords, setLocationModalCoords] = useState<
    { lat: number; lng: number } | undefined
  >();
  const [locationModalIndex, setLocationModalIndex] = useState<number | null>(null);
  const [locationModalSavedLocationId, setLocationModalSavedLocationId] = useState<
    Id | undefined
  >(undefined);
  /**
   * The Maps link already on the stop being saved as a place, so the saved
   * location inherits the same provenance instead of asking for it twice.
   */
  const [locationModalLocationUrl, setLocationModalLocationUrl] = useState<string | undefined>(
    undefined,
  );

  // Handle save location request from LocationCard (create new saved location)
  const handleSaveLocationRequested = useCallback(
    (coordinates: { lat: number; lng: number }, index?: number | null) => {
      // Use provided index when available; otherwise find by coordinates
      const targetIndex =
        index !== undefined && index !== null && index >= 0
          ? index
          : locations.findIndex(location => {
              const latDiff = Math.abs(location.cords.lat - coordinates.lat);
              const lngDiff = Math.abs(location.cords.lng - coordinates.lng);
              return latDiff < 0.0001 && lngDiff < 0.0001;
            });

      // If coords are (0,0) (e.g. first open before locations are hydrated), use map center so the modal doesn't show 0,0
      const isInvalidCoords = coordinates.lat === 0 && coordinates.lng === 0;
      const coordsToUse =
        isInvalidCoords && map
          ? (() => {
              const center = map.getCenter();
              return center ? { lat: center.lat(), lng: center.lng() } : coordinates;
            })()
          : coordinates;

      setLocationModalCoords(coordsToUse);
      setLocationModalSavedLocationId(undefined);
      setLocationModalIndex(targetIndex >= 0 ? targetIndex : null);
      setLocationModalLocationUrl(
        targetIndex >= 0 ? locations[targetIndex]?.locationUrl : undefined,
      );
      setIsLocationModalOpen(true);
    },
    [locations, map],
  );

  // Handle edit saved location request from LocationCard (open modal in edit mode)
  const handleEditSavedLocationRequested = useCallback(
    (savedLocationId: Id, index: number) => {
      setLocationModalSavedLocationId(savedLocationId);
      setLocationModalCoords(undefined);
      setLocationModalLocationUrl(undefined);
      setLocationModalIndex(index >= 0 && index < locations.length ? index : null);
      setIsLocationModalOpen(true);
    },
    [locations.length],
  );

  // Handle location saved - update the location card with saved location data
  const handleLocationSaved = useCallback(
    (savedLocation: SavedLocation) => {
      if (!savedLocation?.address?.location) {
        setIsLocationModalOpen(false);
        setLocationModalCoords(undefined);
        setLocationModalIndex(null);
        return;
      }

      // Convert SavedLocation coordinates to Google Maps format
      const savedCoords = convertToLatLng(savedLocation.address.location);

      // Determine which location to update
      let targetIndex = locationModalIndex;

      // If we don't have a stored index, try to find by coordinates
      if (targetIndex === null || targetIndex < 0) {
        // First try to find by the coordinates we passed to the modal
        if (locationModalCoords) {
          targetIndex = locations.findIndex(location => {
            const latDiff = Math.abs(location.cords.lat - locationModalCoords.lat);
            const lngDiff = Math.abs(location.cords.lng - locationModalCoords.lng);
            return latDiff < 0.0001 && lngDiff < 0.0001;
          });
        }

        // If not found, try to find by saved location coordinates
        if (targetIndex === -1) {
          targetIndex = locations.findIndex(location => {
            const latDiff = Math.abs(location.cords.lat - savedCoords.lat);
            const lngDiff = Math.abs(location.cords.lng - savedCoords.lng);
            return latDiff < 0.0001 && lngDiff < 0.0001;
          });
        }

        // If still not found, use the last location (for newly created locations)
        if (targetIndex === -1 && locations.length > 0) {
          targetIndex = locations.length - 1;
        }
      }

      // If we found a matching location, update it with saved location data
      const index =
        targetIndex !== null && targetIndex >= 0 && targetIndex < locations.length
          ? targetIndex
          : null;
      if (index !== null) {
        const existingLocation = locations[index];
        const updatedLocation: MapLocationData = {
          ...existingLocation,
          cords: savedCoords, // Use coordinates from saved location (might have changed in modal)
          savedLocation: savedLocation, // Full saved location object with name, contacts, etc.
          savedLocationId: savedLocation?._id, // Explicit id for API converter
          city: savedLocation.address.city?.name,
          fullAddress: savedLocation.address.fullAddress,
          address: savedLocation.address,
          isAccurateLocation: true, // Saved locations are accurate
        };

        // Update location using setLocations
        setLocations(prev => {
          const newLocations = [...prev];
          newLocations[index] = updatedLocation;
          return newLocations;
        });

        // Notify parent of the change
        if (onLocationChange) {
          onLocationChange(updatedLocation, index);
        }
      }

      // Exit edit mode on the card that was being edited so it no longer shows edit state
      exitEditMode();
      if (onLocationEditEnd) {
        onLocationEditEnd();
      }

      // Close modal
      setIsLocationModalOpen(false);
      setLocationModalCoords(undefined);
      setLocationModalIndex(null);
      setLocationModalSavedLocationId(undefined);
      setLocationModalLocationUrl(undefined);
    },
    [
      locations,
      locationModalCoords,
      locationModalIndex,
      setLocations,
      onLocationChange,
      exitEditMode,
      onLocationEditEnd,
    ],
  );

  // Save location is available when modal is used (LocationEditModal uses api-hooks internally)
  const isSaveLocationAvailable = true;

  return {
    // State
    locations,
    setLocations,
    editingIndex,
    markerCountLimit,

    // State methods
    addLocation,
    enterEditMode,
    exitEditMode,

    // Handlers
    handleLocationSelect,
    handleCardClick,
    handleAccurateLocationToggle,
    handleLocationEdit,
    handleLocationCancel,
    handleLocationDone,

    // Internal ref (for cancel functionality)
    originalLocationRef,

    // Save location functionality
    handleSaveLocationRequested,
    handleLocationSaved,
    isLocationModalOpen,
    locationModalCoords,
    locationModalIndex,
    closeLocationModal: () => {
      setIsLocationModalOpen(false);
      setLocationModalCoords(undefined);
      setLocationModalIndex(null);
      setLocationModalSavedLocationId(undefined);
      setLocationModalLocationUrl(undefined);
    },
    locationModalSavedLocationId,
    locationModalLocationUrl,
    handleEditSavedLocationRequested,
    orgId,
    isSaveLocationAvailable,
  };
}
